import { z } from 'zod';
import { logoAnchorSchema } from './artwork.js';
import { gameTrailerSchema } from './trailers.js';
import {
  gameFeatureIdSchema,
  gameLanguageSchema,
  systemRequirementsSchema,
} from './gameDetails.js';
import {
  hashSchema,
  platformSchema,
  publicUserSchema,
  slugSchema,
  timestampSchema,
  ulidSchema,
} from './common.js';
import { gameCreditSchema } from './credits.js';

/**
 * How finished a thing is, most finished first.
 *
 * The order is the ladder: it drives the tag colours, the filter strip and the admin picker,
 * so a new rung only has to be added here.
 */
export const GAME_KINDS = ['game', 'early_access', 'dev', 'prototype'] as const;
export const gameKindSchema = z.enum(GAME_KINDS);
export type GameKind = z.infer<typeof gameKindSchema>;

export const GAME_KIND_LABELS: Record<GameKind, string> = {
  game: 'Game',
  early_access: 'Early Access',
  dev: 'Dev',
  prototype: 'Prototype',
};

/** The one line under each choice in the admin picker, so the rungs are not guessed at. */
export const GAME_KIND_HINTS: Record<GameKind, string> = {
  game: 'Finished and meant to be played as it is.',
  early_access: 'Playable start to finish, still being added to.',
  dev: 'Being worked on in the open. Expect rough edges.',
  prototype: 'An idea we are testing. It may never become a game.',
};

/**
 * What an unfinished rung actually costs the person about to install it.
 *
 * The tag names the rung; this says what the word means in practice, because "Dev" and
 * "Prototype" are studio words and nobody outside a studio has to know what they promise. It
 * is deliberately blunt: a supporter paying for everything we make should find out that a
 * build can be unplayable here, on the page, and not by launching it.
 *
 * A finished game has none. Warning every screen about everything teaches people to read past
 * the warnings, which costs us the three that matter.
 */
export interface GameKindWarning {
  /** The line the banner is built around. Said in full, so it survives being read alone. */
  headline: string;
  /** The detail underneath it: what breaks, and what we are and are not promising. */
  body: string;
}

export const GAME_KIND_WARNINGS: Record<GameKind, GameKindWarning | null> = {
  game: null,
  early_access: {
    headline: 'Close to good, still rough.',
    body: 'This one plays start to finish and we are close to something we are happy with, but it is not done. Expect unfinished corners, and bugs bad enough to end a run. Tell us what you hit and it gets fixed.',
  },
  dev: {
    headline: 'In development. It may be broken right now.',
    body: 'We intend to finish this game, and we are building it in the open, so what you install is the work exactly as it stands today. It can crash, it can be unplayable for a build or two, and features can stop halfway through. That is normal here, not a surprise.',
  },
  prototype: {
    headline: 'A prototype. It may not work at all.',
    body: 'This is an idea we are testing, not a game. It may fail to start, break the moment you touch it, or do nothing much. We may drop it and never finish it — a prototype being here is not a promise that it becomes anything.',
  },
};

/** What to say on a game nobody can buy, in place of a price. */
export const SUBSCRIPTION_ONLY_NOTE: Record<GameKind, string | null> = {
  game: null,
  early_access: null,
  dev: 'In the membership. Not sold on its own while it is still being built.',
  prototype: 'In the membership. Prototypes are not sold: they may never become games.',
};

export const BUILD_STATUSES = [
  'uploading',
  'verifying',
  'ready',
  'published',
  'superseded',
  'failed',
] as const;
export const buildStatusSchema = z.enum(BUILD_STATUSES);
export type BuildStatus = z.infer<typeof buildStatusSchema>;

/** What the library shows per platform for a game. */
export const gamePlatformBuildSchema = z.object({
  platform: platformSchema,
  buildId: ulidSchema,
  versionLabel: z.string(),
  totalBytes: z.number().int(),
  publishedAt: timestampSchema,
});
export type GamePlatformBuild = z.infer<typeof gamePlatformBuildSchema>;

/** One picture in a game's gallery. */
export const gameScreenshotSchema = z.object({
  id: ulidSchema,
  url: z.string(),
  caption: z.string().nullable(),
});
export type GameScreenshot = z.infer<typeof gameScreenshotSchema>;

export const gameSchema = z.object({
  id: ulidSchema,
  slug: slugSchema,
  title: z.string(),
  kind: gameKindSchema,
  shortDescription: z.string(),
  /**
   * The long description, as Steam store BBCode.
   *
   * Stored the way it was pasted in, so the same text goes back into Steamworks unchanged. See
   * `bbcode.ts` for what renders and why nothing is converted.
   */
  descriptionBb: z.string(),
  /** The portrait library capsule, 2:3. The shelf tile draws this one. */
  capsuleImageUrl: z.string().nullable(),
  /** The header capsule, 92:43. Wide rows, and what a tile falls back to. */
  coverImageUrl: z.string().nullable(),
  /** The library hero, 96:31. The featured banner and the top of the game page. */
  heroImageUrl: z.string().nullable(),
  /** The title as artwork, drawn over the hero. Null falls back to setting it in type. */
  logoImageUrl: z.string().nullable(),
  /** Where the logo sits on the hero, and how wide it is drawn there. */
  logoAnchor: logoAnchorSchema,
  logoWidthPct: z.number().int(),
  /** The small capsule, 77:29. Search results and short rows. */
  smallImageUrl: z.string().nullable(),
  /** The page background, 16:9, blurred back behind the game page. */
  backgroundImageUrl: z.string().nullable(),
  /**
   * The colour this game lends the room, `#rrggbb`, taken from its own artwork when it was
   * uploaded. Null until a picture has been through the admin panel; `accentOf` in `accent.ts`
   * is what every screen should ask, because it answers for that case too.
   */
  accentColor: z.string().nullable(),
  /** The gallery, in the order the admin put them in. */
  screenshots: z.array(gameScreenshotSchema),
  /**
   * The trailers, in the order the admin put them in.
   *
   * They draw in the same gallery as the screenshots and before them, which is where a store
   * page puts a trailer and where somebody deciding on a game looks first.
   */
  trailers: z.array(gameTrailerSchema),

  androidPackageName: z.string().nullable(),
  /** Where else the game can be bought. Null on both for most games. See `stores.ts`. */
  steamUrl: z.string().nullable(),
  epicUrl: z.string().nullable(),
  /* The facts panel. Every one of these is a question a store page answers and ours could not
     until now; see `gameDetails.ts` for why they are shaped the way they are. */

  /** Who made it, and who put it out. Usually the same name, and often the studio's own. */
  developerName: z.string().nullable(),
  publisherName: z.string().nullable(),
  /** The game's own site, when it has one that is not a storefront. */
  websiteUrl: z.string().nullable(),
  /** `YYYY`, `YYYY-MM` or `YYYY-MM-DD`. Null for a game with no date yet. */
  releaseDate: z.string().nullable(),
  /** What to say instead of, or alongside, a date. "When it is ready" lives here. */
  releaseNote: z.string().nullable(),
  /** The words it is filed under. Free text, in the order the admin put them in. */
  genres: z.array(z.string()),
  /** Single-player, online co-op, full controller support, and the rest. Always table order. */
  features: z.array(gameFeatureIdSchema),
  /** What it is menued, spoken and subtitled in. */
  languages: z.array(gameLanguageSchema),
  /** Minimum and recommended, per device. `{}` for a game that has not said. */
  systemRequirements: systemRequirementsSchema,
  /** What is in it that somebody might want to know before installing it. */
  contentNotice: z.string().nullable(),
  /** The copyright and trademark line, drawn small at the foot of the page. */
  legalNotice: z.string().nullable(),

  devs: z.array(publicUserSchema),
  /**
   * The people at the studio who worked on it, and what each of them did.
   *
   * Not `devs`, which is a link between a game and a GOOD BUILD account and exists so a member
   * of staff shows as staff. This is the credit roll: a named person, the roles they held on
   * this game and a line about what that meant. Somebody can be in one, the other or both.
   */
  credits: z.array(gameCreditSchema),
  /** One entry per platform that has a published current build. */
  builds: z.array(gamePlatformBuildSchema),

  sortOrder: z.number().int(),
  published: z.boolean(),
});
export type Game = z.infer<typeof gameSchema>;

/** `GET /v1/games/:slug/builds/current?platform=` */
export const currentBuildSchema = z.object({
  buildId: ulidSchema,
  gameId: ulidSchema,
  platform: platformSchema,
  versionLabel: z.string(),
  manifestUrl: z.string().url(),
  manifestExpiresAt: timestampSchema,
  totalBytes: z.number().int(),
  fileCount: z.number().int(),
  chunkCount: z.number().int(),
  publishedAt: timestampSchema,
});
export type CurrentBuild = z.infer<typeof currentBuildSchema>;

/** `GET /v1/library/updates?installed=gameId:buildId,...` */
export const updatesQuerySchema = z.object({
  installed: z.string().max(8192).optional().describe('comma separated gameId:buildId pairs'),
  platform: platformSchema,
});

export const updateEntrySchema = z.object({
  gameId: ulidSchema,
  slug: slugSchema,
  title: z.string(),
  platform: platformSchema,
  installedBuildId: ulidSchema.nullable(),
  currentBuildId: ulidSchema,
  versionLabel: z.string(),
  totalBytes: z.number().int(),
  publishedAt: timestampSchema,
});
export type UpdateEntry = z.infer<typeof updateEntrySchema>;

export const updatesResponseSchema = z.object({
  updates: z.array(updateEntrySchema),
  /** Games the client has installed that are no longer published. */
  unpublished: z.array(ulidSchema),
});
export type UpdatesResponse = z.infer<typeof updatesResponseSchema>;

/** `POST /v1/downloads/urls` */
export const downloadUrlsBodySchema = z.object({
  buildId: ulidSchema,
  chunkIds: z.array(hashSchema).min(1),
});
export type DownloadUrlsBody = z.infer<typeof downloadUrlsBodySchema>;

export const downloadUrlsResponseSchema = z.object({
  expiresAt: timestampSchema,
  urls: z.array(z.object({ chunkId: hashSchema, url: z.string().url() })),
});
export type DownloadUrlsResponse = z.infer<typeof downloadUrlsResponseSchema>;
